import { BillingTransactionIdentifier, BillingTransactionIdentifierErrorResponseData } from "@/common/types/billing/transactionIdentifier.type";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { refundSchema, RefundValidationSchema } from "../schemas/refund.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import useCreateRefund from "./useCreateRefund";
import useDebounce from "@/common/hooks/useDebounce";
import { useBillingTransactionIdentifiers } from "./useBillingTransactionIdentifiers";

export const useRefundForm = () => {
  const [modalPasswordIsOpen, setModalPasswordIsOpen] = useState(false);
  const [modalSuccessData, setModalSuccessData] = useState<BillingTransactionIdentifier | boolean>(false);
  const [modalErrorData, setModalErrorData] = useState<BillingTransactionIdentifierErrorResponseData | boolean>(false);

  const formMethods = useForm<RefundValidationSchema>({
    resolver: zodResolver(refundSchema),
    defaultValues: {
      transaction_identifier: "",
      reason: "",
    },
  });

  const {
    watch,
    setValue,
  } = formMethods;

  const {
    mutate: createRefund,
    isPending: createRefundIsPending,
  } = useCreateRefund({
    onSuccess: (response) => {
      setModalSuccessData(response.data.data);
    },
    onError: (error) => {
      setValue('consumption_password', '');
      setModalErrorData(error);
    },
  });

  const onSubmit: SubmitHandler<RefundValidationSchema> = () => setModalPasswordIsOpen(true);

  const watchEventId = watch('transaction_identifier');

  const [transactionIdentifierDebounced, setTransactionIdentifierDebounced] = useDebounce('');

  useEffect(() => {
    setTransactionIdentifierDebounced(watchEventId);
  }, [watchEventId]);

  const {
    data: transactionIdentifiers,
    isLoading: transactionIdentifiersIsLoading,
    isError: transactionIdentifiersIsError,
    isPending: transactionIdentifiersIsPending
  } = useBillingTransactionIdentifiers<BillingTransactionIdentifier>({
    identifier: transactionIdentifierDebounced || '',
  });

  return {
    modalPasswordIsOpen,
    setModalPasswordIsOpen,
    modalSuccessData,
    setModalSuccessData,
    modalErrorData,
    setModalErrorData,
    formMethods,
    createRefund,
    createRefundIsPending,
    onSubmit,
    transactionIdentifiers,
    transactionIdentifiersIsLoading,
    transactionIdentifiersIsError,
    transactionIdentifiersIsPending,
  }
}
